import { Input, Modal } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import { useCreateTheme, useRenameTheme } from '../hooks/use-themes';
import type { Theme } from '../types/theme';

export type ThemeNameModalState = { mode: 'create' | 'rename'; theme?: Theme } | null;

export function ThemeNameModal({
  state,
  onClose,
}: {
  state: ThemeNameModalState;
  onClose: () => void;
}) {
  const { notificationHandler } = useHandlers();
  const createTheme = useCreateTheme();
  const renameTheme = useRenameTheme();

  const isRename = state?.mode === 'rename';
  const [name, setName] = useState<string>('');

  // Reset the input whenever the modal is (re)opened.
  useEffect(() => {
    if (state) {
      setName(state.theme?.name ?? '');
    }
  }, [state]);

  const mutation = isRename ? renameTheme : createTheme;

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }

    try {
      if (isRename && state?.theme) {
        await renameTheme.mutateAsync({ id: state.theme.id, payload: { name: trimmed } });
      } else {
        await createTheme.mutateAsync({ name: trimmed });
      }
      onClose();
    } catch {
      notificationHandler.error();
    }
  }

  return (
    <Modal
      isOpen={state !== null}
      onClose={onClose}
      primaryAction={{
        text: isRename ? t('buttons:save') : t('buttons:create'),
        onClick: () => void handleSubmit(),
        disabled: !name.trim() || mutation.isPending,
        loading: mutation.isPending,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: onClose,
      }}
    >
      <Modal.Header>
        {isRename
          ? t('features:themes.pages.themes_list_page.modals.rename.title')
          : t('features:themes.pages.themes_list_page.modals.create.title')}
      </Modal.Header>
      <Modal.Body>
        <Input
          label={t('features:themes.pages.themes_list_page.modals.name_label')}
          value={name}
          onChange={setName}
        />
      </Modal.Body>
    </Modal>
  );
}
